import React from 'react';
import { Home, UsersRound, Users, BookOpen, CircleUser } from 'lucide-react';
import type { PlaceMatchView } from '../../types';

interface BottomNavBarProps {
  currentView: PlaceMatchView;
  onNavigate: (view: PlaceMatchView) => void;
  isLoggedIn: boolean;
  onAuthRequired: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  view: PlaceMatchView | null;
  requiresAuth: boolean;
}

const navItems: NavItem[] = [
  { id: 'home', label: 'Orte', icon: Home, view: 'search', requiresAuth: false },
  { id: 'groups', label: 'Gruppen', icon: UsersRound, view: 'groups', requiresAuth: true },
  { id: 'community', label: 'Community', icon: Users, view: 'community', requiresAuth: true },
  { id: 'magazine', label: 'Magazin', icon: BookOpen, view: 'magazine', requiresAuth: false },
  { id: 'profile', label: 'Profil', icon: CircleUser, view: 'privacySettings', requiresAuth: true },
];

// Views that should highlight the "Orte" tab
const homeViews: PlaceMatchView[] = [
  'search', 'search-map', 'detail', 'favorites', 'create', 'createIntro', 'myPlaces', 'logbook'
];

// Views that should highlight the "Gruppen" tab
const groupViews: PlaceMatchView[] = [
  'groups', 'groupDetail', 'createGroup', 'myGroups'
];

// Views that should highlight the "Community" tab
const communityViews: PlaceMatchView[] = [
  'community', 'communityEvents', 'publicProfile', 'userSearch', 'messages', 'chat'
];

// Views that should highlight the "Magazin" tab
const magazineViews: PlaceMatchView[] = [
  'magazine', 'articleDetail'
];

// Views that should highlight the "Profil" tab
const profileViews: PlaceMatchView[] = [
  'privacySettings'
];

function getActiveTab(view: PlaceMatchView): string {
  if (homeViews.includes(view)) return 'home';
  if (groupViews.includes(view)) return 'groups';
  if (communityViews.includes(view)) return 'community';
  if (magazineViews.includes(view)) return 'magazine';
  if (profileViews.includes(view)) return 'profile';
  return 'home';
}

export function BottomNavBar({
  currentView,
  onNavigate,
  isLoggedIn,
  onAuthRequired,
}: BottomNavBarProps) {
  const activeTab = getActiveTab(currentView);

  const handleNavClick = (item: NavItem) => {
    // Check auth for protected views
    if (item.requiresAuth && !isLoggedIn) {
      onAuthRequired();
      return;
    }

    // Navigate to view
    if (item.view) {
      onNavigate(item.view);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 md:hidden">
      <div
        className="flex items-center justify-around h-16"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {navItems.map((item) => {
          const isActive = item.id === activeTab;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={`
                flex flex-col items-center justify-center flex-1 h-full
                transition-colors duration-200
                ${isActive
                  ? 'text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon
                className={`w-6 h-6 ${isActive ? 'stroke-[2.5]' : 'stroke-[1.5]'}`}
                fill={isActive ? 'currentColor' : 'none'}
              />
              <span className={`text-xs mt-1 ${isActive ? 'font-medium' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
